/**
 * Yumz share-cards v2 — canvas + share helpers.
 *
 * Shared between renderCheckIn (Flow A) and renderVerdict (Flow B).
 * All drawing primitives + the cross-platform Share dispatch live here.
 *
 * Performance principles:
 *  - No web fonts (system stack only).
 *  - No remote asset fetches beyond the user/dish photos the caller supplies.
 *  - The `preloaded` map carries already-decoded DOM <img> refs so canvas
 *    composition reuses them instead of re-decoding ~80–120KB data: URLs.
 *  - On the web Share path we use canvas.toBlob (avoids the toDataURL → fetch
 *    → blob roundtrip and the giant base64 string in memory). On native we
 *    still need base64 because Filesystem.writeFile requires it.
 */

import type { ShareStatus, StatusDefinition } from "./shareStatus";
import { SHARE_STATUSES } from "./shareStatus";
import { fmtErr, isUserCancellation } from "@/lib/utils";

export type PreloadedImages =
  | Map<string, HTMLImageElement>
  | Record<string, HTMLImageElement>;

// ─── image loading ──────────────────────────────────────────────────

export function loadImage(
  url: string,
  cache: Map<string, Promise<HTMLImageElement>>,
  preloaded?: PreloadedImages,
): Promise<HTMLImageElement> {
  if (preloaded) {
    const direct =
      preloaded instanceof Map ? preloaded.get(url) : preloaded[url];
    if (direct && direct.complete && direct.naturalWidth > 0) {
      return Promise.resolve(direct);
    }
  }
  const existing = cache.get(url);
  if (existing) return existing;
  const p = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`shareCardHelpers.loadImage: ${url.slice(0, 80)}`));
    img.src = url;
  });
  cache.set(url, p);
  return p;
}

// ─── geometry primitives ────────────────────────────────────────────

export function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

export function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
): void {
  ctx.save();
  ctx.fillStyle = fill;
  roundedPath(ctx, x, y, w, h, r);
  ctx.fill();
  ctx.restore();
}

export function strokeRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  stroke: string,
  width = 2,
): void {
  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = width;
  roundedPath(ctx, x, y, w, h, r);
  ctx.stroke();
  ctx.restore();
}

// ─── text helpers ───────────────────────────────────────────────────

export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  align: CanvasTextAlign = "center",
): number {
  ctx.save();
  ctx.textAlign = align;
  const lines = wrapText(ctx, text, maxWidth);
  let cy = y;
  for (const line of lines) {
    ctx.fillText(line, x, cy);
    cy += lineHeight;
  }
  ctx.restore();
  return cy;
}

export const SYSTEM_DISPLAY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif';
export const SYSTEM_BODY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
export const SYSTEM_HANDSCRIPT =
  '"Bradley Hand", "Marker Felt", "Apple Color Emoji", -apple-system, sans-serif';

// ─── grain / noise overlay ──────────────────────────────────────────

export function drawGrain(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  density = 0.35,
  alpha = 0.08,
): void {
  const tw = Math.max(1, Math.round(w / 4));
  const th = Math.max(1, Math.round(h / 4));
  const off = document.createElement("canvas");
  off.width = tw;
  off.height = th;
  const octx = off.getContext("2d");
  if (!octx) return;
  const img = octx.createImageData(tw, th);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    if (Math.random() < density) {
      const v = (Math.random() * 255) | 0;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 255;
    } else {
      data[i + 3] = 0;
    }
  }
  octx.putImageData(img, 0, 0);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "overlay";
  ctx.drawImage(off, 0, 0, w, h);
  ctx.restore();
}

// ─── status pill ────────────────────────────────────────────────────

export function drawStatusPill(
  ctx: CanvasRenderingContext2D,
  status: ShareStatus,
  cx: number,
  cy: number,
  fontPx = 28,
  opts: { includeEmoji?: boolean; align?: "center" | "left" | "right" } = {},
): number {
  const def = SHARE_STATUSES[status];
  const includeEmoji = opts.includeEmoji ?? true;
  const align = opts.align ?? "center";
  const label = (includeEmoji ? `${def.emoji}  ` : "") + def.label.toUpperCase();
  ctx.save();
  ctx.font = `800 ${fontPx}px ${SYSTEM_BODY}`;
  const padX = Math.round(fontPx * 0.9);
  const padY = Math.round(fontPx * 0.55);
  const textW = ctx.measureText(label).width;
  const w = Math.round(textW + padX * 2);
  const h = Math.round(fontPx + padY * 2);
  let x = cx;
  if (align === "center") x = cx - w / 2;
  else if (align === "right") x = cx - w;
  const y = cy - h / 2;
  fillRoundedRect(ctx, x, y, w, h, h / 2, def.pill.bg);
  if (def.pill.border) {
    strokeRoundedRect(ctx, x, y, w, h, h / 2, def.pill.border, 2);
  }
  ctx.fillStyle = def.pill.color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2 + 1);
  ctx.restore();
  return w;
}

// ─── polaroid mat ───────────────────────────────────────────────────

export function drawPolaroidMat(
  ctx: CanvasRenderingContext2D,
  photo: HTMLImageElement,
  cx: number,
  cy: number,
  matW: number,
  matH: number,
  tiltDeg = -4,
  caption?: string,
): void {
  const tilt = (tiltDeg * Math.PI) / 180;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tilt);

  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 32;
  ctx.shadowOffsetY = 14;
  ctx.fillStyle = "#ffffff";
  const mx = -matW / 2;
  const my = -matH / 2;
  fillRoundedRect(ctx, mx, my, matW, matH, 6, "#ffffff");
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  const padX = Math.round(matW * 0.06);
  const padTop = Math.round(matW * 0.06);
  const padBottom = Math.round(matH * 0.18);
  const pw = matW - padX * 2;
  const ph = matH - padTop - padBottom;
  const px = mx + padX;
  const py = my + padTop;

  ctx.save();
  roundedPath(ctx, px, py, pw, ph, 2);
  ctx.clip();
  const scale = Math.max(pw / photo.width, ph / photo.height);
  const dw = photo.width * scale;
  const dh = photo.height * scale;
  ctx.drawImage(photo, px + (pw - dw) / 2, py + (ph - dh) / 2, dw, dh);
  ctx.restore();

  if (caption) {
    ctx.fillStyle = "#2a1a00";
    ctx.font = `italic 500 ${Math.round(matW * 0.085)}px ${SYSTEM_HANDSCRIPT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const captionY = py + ph + padBottom / 2;
    ctx.fillText(caption, mx + matW / 2, captionY);
  }

  ctx.restore();
}

// ─── status background atmospheres ──────────────────────────────────

export function drawStatusAtmosphere(
  ctx: CanvasRenderingContext2D,
  status: ShareStatus,
  w: number,
  h: number,
): void {
  const def = SHARE_STATUSES[status];

  switch (def.texture) {
    case "neon": {
      const base = ctx.createLinearGradient(0, 0, w, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(0.45, "#6b1500");
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      const top = ctx.createRadialGradient(w * 0.5, 0, 0, w * 0.5, 0, w * 0.7);
      top.addColorStop(0, "rgba(255,210,80,0.55)");
      top.addColorStop(1, "rgba(255,210,80,0)");
      ctx.fillStyle = top;
      ctx.fillRect(0, 0, w, h);

      const br = ctx.createRadialGradient(w, h, 0, w, h, w * 0.9);
      br.addColorStop(0, "rgba(255,50,0,0.85)");
      br.addColorStop(1, "rgba(255,50,0,0)");
      ctx.fillStyle = br;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      const angle = -25 * Math.PI / 180;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const spacing = 110;
      const max = Math.max(w, h) * 2;
      for (let i = -max; i < max; i += spacing) {
        ctx.beginPath();
        ctx.moveTo(i * cosA - max * sinA, i * sinA + max * cosA);
        ctx.lineTo(i * cosA + max * sinA, i * sinA - max * cosA);
        ctx.stroke();
      }
      ctx.restore();
      break;
    }

    case "glow": {
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      const blob1 = ctx.createRadialGradient(w * 0.25, h * 0.3, 0, w * 0.25, h * 0.3, w * 0.6);
      blob1.addColorStop(0, "rgba(168,85,247,0.65)");
      blob1.addColorStop(1, "rgba(168,85,247,0)");
      ctx.fillStyle = blob1;
      ctx.fillRect(0, 0, w, h);

      const blob2 = ctx.createRadialGradient(w * 0.8, h * 0.7, 0, w * 0.8, h * 0.7, w * 0.55);
      blob2.addColorStop(0, "rgba(236,72,153,0.55)");
      blob2.addColorStop(1, "rgba(236,72,153,0)");
      ctx.fillStyle = blob2;
      ctx.fillRect(0, 0, w, h);

      const blob3 = ctx.createRadialGradient(w * 0.55, h * 0.92, 0, w * 0.55, h * 0.92, w * 0.5);
      blob3.addColorStop(0, "rgba(99,102,241,0.55)");
      blob3.addColorStop(1, "rgba(99,102,241,0)");
      ctx.fillStyle = blob3;
      ctx.fillRect(0, 0, w, h);
      break;
    }

    case "paper": {
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.strokeStyle = "rgba(26,18,7,0.045)";
      ctx.lineWidth = 1;
      const gap = Math.round(w / 18);
      for (let x = 0; x < w; x += gap) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += gap) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      ctx.restore();
      break;
    }

    case "deadpan": {
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = "rgba(255,255,255,0.08)";
      const spacing = Math.round(w / 28);
      const r = 2;
      for (let y = spacing; y < h; y += spacing) {
        for (let x = spacing; x < w; x += spacing) {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    }

    case "parchment": {
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(0.6, "#1a1207");
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      const warm = ctx.createRadialGradient(w * 0.5, h, 0, w * 0.5, h, w * 0.85);
      warm.addColorStop(0, "rgba(255,200,80,0.35)");
      warm.addColorStop(1, "rgba(255,200,80,0)");
      ctx.fillStyle = warm;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.strokeStyle = "rgba(212,175,55,0.45)";
      ctx.lineWidth = 4;
      const inset = Math.round(w * 0.03);
      ctx.strokeRect(inset, inset, w - inset * 2, h - inset * 2);
      ctx.restore();
      break;
    }

    case "candle": {
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(0.6, "#3d1e0a");
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      const top = ctx.createRadialGradient(w * 0.5, h * 0.1, 0, w * 0.5, h * 0.1, w * 0.55);
      top.addColorStop(0, "rgba(255,200,140,0.85)");
      top.addColorStop(1, "rgba(255,200,140,0)");
      ctx.fillStyle = top;
      ctx.fillRect(0, 0, w, h);

      const bl = ctx.createRadialGradient(0, h, 0, 0, h, w * 0.8);
      bl.addColorStop(0, "rgba(180,100,40,0.6)");
      bl.addColorStop(1, "rgba(180,100,40,0)");
      ctx.fillStyle = bl;
      ctx.fillRect(0, 0, w, h);
      break;
    }

    case "midnight": {
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, def.palette.bg1);
      base.addColorStop(1, def.palette.bg2);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      const g1 = ctx.createRadialGradient(w * 0.8, h * 0.8, 0, w * 0.8, h * 0.8, w * 0.6);
      g1.addColorStop(0, "rgba(54,131,99,0.35)");
      g1.addColorStop(1, "rgba(54,131,99,0)");
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, w, h);

      const g2 = ctx.createRadialGradient(w * 0.2, h * 0.2, 0, w * 0.2, h * 0.2, w * 0.55);
      g2.addColorStop(0, "rgba(60,80,150,0.25)");
      g2.addColorStop(1, "rgba(60,80,150,0)");
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, w, h);
      break;
    }
  }
}

// ─── brand stamp ────────────────────────────────────────────────────

export function drawYumzStamp(
  ctx: CanvasRenderingContext2D,
  status: ShareStatus,
  w: number,
  h: number,
): void {
  const def = SHARE_STATUSES[status];
  ctx.save();
  ctx.font = `800 ${Math.round(w * 0.022)}px ${SYSTEM_BODY}`;
  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  const isLightBg = def.texture === "paper";
  ctx.fillStyle = isLightBg ? "rgba(26,18,7,0.55)" : "rgba(255,255,255,0.5)";
  ctx.fillText("YUMZ", w - Math.round(w * 0.035), h - Math.round(h * 0.026));
  ctx.font = `500 ${Math.round(w * 0.014)}px ${SYSTEM_BODY}`;
  ctx.fillStyle = isLightBg ? "rgba(26,18,7,0.4)" : "rgba(255,255,255,0.4)";
  ctx.fillText("yumz.social", w - Math.round(w * 0.035), h - Math.round(h * 0.012));
  ctx.restore();
}

// ─── canvas → Blob ──────────────────────────────────────────────────

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  quality = 0.88,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvasToBlob: null"))),
      "image/jpeg",
      quality,
    );
  });
}

// ─── cross-platform share dispatch ──────────────────────────────────

export async function dispatchShare(
  blob: Blob,
  shareText: string,
  fileNameBase: string,
): Promise<void> {
  const { Capacitor } = await import("@capacitor/core");

  // ── Native (Capacitor) path ──────────────────────────────────────
  if (Capacitor.isNativePlatform()) {
    try {
      const { Share } = await import("@capacitor/share");
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const base64Data = await blobToBase64(blob);
      const fileName = `${fileNameBase}-${Date.now()}.jpg`;
      const written = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
      });
      await Share.share({
        title: shareText,
        text: shareText,
        url: written.uri,
        dialogTitle: "share",
      });
      return;
    } catch (nativeErr) {
      // User cancelled — not a real error
      if (isUserCancellation(nativeErr)) throw nativeErr;
      // Real failure — fall through to Web Share / download
      console.warn("[dispatchShare] native share failed, trying web fallback:", fmtErr(nativeErr));
    }
  }

  // ── Web Share API path ───────────────────────────────────────────
  if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: unknown }).share) {
    try {
      const file = new File([blob], `${fileNameBase}.jpg`, { type: "image/jpeg" });
      await (navigator as Navigator & { share: (data: { title: string; files: File[] }) => Promise<void> }).share({
        title: shareText,
        files: [file],
      });
      return;
    } catch (webErr) {
      if (isUserCancellation(webErr)) throw webErr;
      console.warn("[dispatchShare] web share failed, falling back to download:", fmtErr(webErr));
    }
  }

  // ── Download fallback ────────────────────────────────────────────
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileNameBase}.jpg`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export type { StatusDefinition };
